/**
 * Policy Viewer Dialog
 *
 * A read-only dialog for viewing policy content.
 * Used in My Hats, My Policies, and other user-facing views.
 * Does NOT include admin features - just displays the policy for reading.
 */

import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { OrgPolicy } from '@newgenus/common';

export interface PolicyViewerDialogData {
    policy: OrgPolicy;
    policyKey: string;
    /** Optional: Show acknowledgment button */
    showAcknowledgeButton?: boolean;
}

export interface PolicyViewerDialogResult {
    action: 'close' | 'acknowledge';
}

@Component({
    selector: 'shared-org-policy-viewer-dialog',
    imports: [MaterialModule, CommonModule],
    template: `
        <div class="policy-viewer-container" [class.fullscreen]="isFullscreen">
            <!-- Header -->
            <div class="viewer-header">
                <div class="header-content">
                    <mat-icon class="policy-icon">policy</mat-icon>
                    <div class="title-section">
                        <h2 class="policy-title">{{ data.policy.name }}</h2>
                        <span class="policy-meta" *ngIf="data.policy.category">
                            {{ formatCategory(data.policy.category) }}
                            <span *ngIf="data.policy.version"> · Version {{ data.policy.version }}</span>
                        </span>
                    </div>
                </div>
                <div class="header-actions">
                    <button
                        mat-icon-button
                        (click)="toggleFullscreen()"
                        [matTooltip]="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'"
                    >
                        <mat-icon>{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
                    </button>
                    <button mat-icon-button (click)="close()" matTooltip="Close">
                        <mat-icon>close</mat-icon>
                    </button>
                </div>
            </div>

            <!-- Policy Content -->
            <div class="policy-content">
                <div class="policy-description" *ngIf="data.policy.description">
                    <mat-icon>info_outline</mat-icon>
                    <p>{{ data.policy.description }}</p>
                </div>

                <div
                    class="policy-body"
                    [innerHTML]="data.policy.policyWriting || '<p>No content available.</p>'"
                ></div>
            </div>

            <!-- Footer -->
            <div class="viewer-footer">
                <div class="footer-info">
                    <span *ngIf="data.policy.effectiveDate" class="effective-date">
                        <mat-icon>event</mat-icon>
                        Effective: {{ formatDate(data.policy.effectiveDate) }}
                    </span>
                </div>
                <div class="footer-actions">
                    <button mat-button (click)="close()">Close</button>
                    <button
                        mat-raised-button
                        color="primary"
                        *ngIf="data.showAcknowledgeButton"
                        (click)="acknowledge()"
                    >
                        <mat-icon>fact_check</mat-icon>
                        Acknowledge
                    </button>
                </div>
            </div>
        </div>
    `,
    styles: [
        `
            .policy-viewer-container {
                display: flex;
                flex-direction: column;
                height: 100%;
                background: #fff;
            }

            .viewer-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
                background: #fafafa;
                flex-shrink: 0;
            }

            .header-content {
                display: flex;
                align-items: center;
                gap: 16px;
            }

            .policy-icon {
                font-size: 32px;
                width: 32px;
                height: 32px;
                color: #ff9800;
            }

            .title-section {
                display: flex;
                flex-direction: column;
            }

            .policy-title {
                margin: 0;
                font-size: 20px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
            }

            .policy-meta {
                font-size: 13px;
                color: rgba(0, 0, 0, 0.54);
                margin-top: 2px;
            }

            .header-actions {
                display: flex;
                gap: 4px;
            }

            .policy-content {
                flex: 1;
                overflow-y: auto;
                padding: 24px;

                /* Styled scrollbar matching app theme */
                &::-webkit-scrollbar {
                    width: 8px;
                }
                &::-webkit-scrollbar-track {
                    background: rgba(0, 0, 0, 0.05);
                    border-radius: 4px;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 4px;
                    &:hover {
                        background: rgba(0, 0, 0, 0.3);
                    }
                }
            }

            .policy-description {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                padding: 16px;
                background: rgba(33, 150, 243, 0.08);
                border-radius: 8px;
                margin-bottom: 24px;

                mat-icon {
                    color: #2196f3;
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                p {
                    margin: 0;
                    color: rgba(0, 0, 0, 0.7);
                    line-height: 1.5;
                }
            }

            .policy-body {
                line-height: 1.7;
                color: rgba(0, 0, 0, 0.87);

                ::ng-deep {
                    h1,
                    h2,
                    h3,
                    h4,
                    h5,
                    h6 {
                        margin-top: 24px;
                        margin-bottom: 12px;
                        font-weight: 500;
                    }

                    h1:first-child,
                    h2:first-child,
                    h3:first-child {
                        margin-top: 0;
                    }

                    p {
                        margin-bottom: 16px;
                    }

                    ul,
                    ol {
                        margin-bottom: 16px;
                        padding-left: 24px;
                    }

                    li {
                        margin-bottom: 8px;
                    }

                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 16px;

                        th,
                        td {
                            border: 1px solid rgba(0, 0, 0, 0.12);
                            padding: 8px 12px;
                            text-align: left;
                        }

                        th {
                            background: #f5f5f5;
                            font-weight: 500;
                        }
                    }

                    blockquote {
                        margin: 16px 0;
                        padding: 12px 20px;
                        border-left: 4px solid #ff9800;
                        background: rgba(255, 152, 0, 0.08);
                        font-style: italic;
                    }

                    a {
                        color: var(--entity-color-policies, #00897b);
                        text-decoration: none;
                        &:hover {
                            text-decoration: underline;
                        }
                    }

                    img {
                        max-width: 100%;
                        height: auto;
                    }

                    code {
                        background: #f5f5f5;
                        padding: 2px 6px;
                        border-radius: 4px;
                        font-family: 'Roboto Mono', monospace;
                    }

                    pre {
                        background: #263238;
                        color: #fff;
                        padding: 16px;
                        border-radius: 8px;
                        overflow-x: auto;

                        code {
                            background: transparent;
                            padding: 0;
                        }
                    }
                }
            }

            .viewer-footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 12px 24px;
                border-top: 1px solid rgba(0, 0, 0, 0.12);
                background: #fafafa;
                flex-shrink: 0;
            }

            .footer-info {
                display: flex;
                align-items: center;
                gap: 16px;
            }

            .effective-date {
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.54);

                mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
            }

            .footer-actions {
                display: flex;
                gap: 8px;
            }

            /* Fullscreen mode */
            :host-context(.policy-viewer-fullscreen) {
                .policy-viewer-container.fullscreen {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    z-index: 1000;
                }
            }

            /* Dark theme support */
            :host-context(.dark-theme) {
                .policy-viewer-container {
                    background: #1e1e1e;
                }

                .viewer-header {
                    border-bottom-color: rgba(255, 255, 255, 0.12);
                    background: #2d2d2d;
                }

                .policy-title {
                    color: rgba(255, 255, 255, 0.87);
                }

                .policy-meta {
                    color: rgba(255, 255, 255, 0.54);
                }

                .policy-content {
                    &::-webkit-scrollbar-track {
                        background: rgba(255, 255, 255, 0.05);
                    }
                    &::-webkit-scrollbar-thumb {
                        background: rgba(255, 255, 255, 0.2);
                        &:hover {
                            background: rgba(255, 255, 255, 0.3);
                        }
                    }
                }

                .policy-description {
                    background: rgba(33, 150, 243, 0.15);

                    p {
                        color: rgba(255, 255, 255, 0.7);
                    }
                }

                .policy-body {
                    color: rgba(255, 255, 255, 0.87);

                    ::ng-deep {
                        table {
                            th,
                            td {
                                border-color: rgba(255, 255, 255, 0.12);
                            }

                            th {
                                background: #333;
                            }
                        }

                        blockquote {
                            background: rgba(255, 152, 0, 0.12);
                        }

                        a {
                            color: var(--entity-color-policies, #00897b);
                        }

                        code {
                            background: #333;
                            color: #e0e0e0;
                        }

                        pre {
                            background: #1a1a1a;
                        }
                    }
                }

                .viewer-footer {
                    border-top-color: rgba(255, 255, 255, 0.12);
                    background: #2d2d2d;
                }

                .effective-date {
                    color: rgba(255, 255, 255, 0.54);
                }
            }
        `,
    ],
})
export class OrgPolicyViewerDialogComponent {
    public isFullscreen = false;

    constructor(
        public dialogRef: MatDialogRef<OrgPolicyViewerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PolicyViewerDialogData
    ) {}

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (this.isFullscreen) {
            this.isFullscreen = false;
        } else {
            this.close();
        }
    }

    public toggleFullscreen(): void {
        this.isFullscreen = !this.isFullscreen;

        // Update dialog size
        if (this.isFullscreen) {
            this.dialogRef.updateSize('100vw', '100vh');
            this.dialogRef.addPanelClass('policy-viewer-fullscreen');
        } else {
            this.dialogRef.updateSize('85vw', '85vh');
            this.dialogRef.removePanelClass('policy-viewer-fullscreen');
        }
    }

    public close(): void {
        this.dialogRef.close({ action: 'close' } as PolicyViewerDialogResult);
    }

    public acknowledge(): void {
        this.dialogRef.close({ action: 'acknowledge' } as PolicyViewerDialogResult);
    }

    public formatCategory(category: string): string {
        if (!category) return '';
        // Convert snake_case or kebab-case to Title Case
        return category.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    public formatDate(timestamp: Date | { toDate(): Date } | string | number | null | undefined): string {
        if (!timestamp) return '';

        const date =
            typeof timestamp === 'object' && 'toDate' in timestamp
                ? timestamp.toDate()
                : new Date(timestamp as string | number);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }
}
